import { Check, Pencil } from "lucide-react";
import type { JSX } from "react";
import { createContext, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppConfig } from "@/common/appConfig";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { stripMarkdown } from "@/lib/plainText";
import { cn } from "@/lib/utils";
import {
  removeVerifyMarker,
  VERIFY_CHIP_CLASS,
  type VerifyMarkerContext,
  verifyMarkerContexts,
} from "@/lib/verifyMarkers";
import { UnreviewedSheet } from "./UnreviewedSheet";

// v3.2 app: the marker as the warn-tint chip and the cards on the v3
// surfaces (no classic amber/emerald fills).
const V3_CHIP =
  "inline-flex items-center gap-1 rounded-full bg-v3-warn-tint px-1.5 align-baseline text-[0.8em] font-semibold leading-snug text-v3-warn-text";
const V3_DONE_CHIP =
  "inline-flex items-center gap-1 rounded-full bg-v3-ok-tint px-1.5 align-baseline text-[0.8em] font-semibold leading-snug text-v3-ok-text";

// Review step before a note leaves ROSC or feeds another document (clinical
// lead, 22/10): while the note still has "[verify]" markers, Copy, section
// copy, Export and creating a patient summary or referral letter open a list
// of the flagged values. Each one is confirmed (the marker goes, the value
// stays and the note is saved) or edited in the editor. The action runs only
// once every marker it covers is resolved.
//
// VITE_REVIEW_COPY_ANYWAY_ENABLED (clinical lead, 28/10): Copy and Export may
// go ahead with unreviewed values. They first open a sheet with the count,
// "Review values" (this list) and "Copy anyway" / "Export anyway". Creating
// a patient summary or referral letter still needs every value resolved.

export type VerifyReviewReason = "copy" | "export" | "summary" | "letter";

export interface VerifyGatedAction {
  reason: VerifyReviewReason;
  // The primary button once everything is resolved ("Copy note").
  label: string;
  // The markers that block this action, as [first, first + count) of the
  // whole note's markers. Omitted: all of them (whole note).
  scope?: (text: string) => { first: number; count: number };
  // Runs with the reviewed note (the markers in scope resolved).
  proceed: (text: string) => void;
}

export interface VerifyGate {
  request: (action: VerifyGatedAction) => void;
}

const VerifyGateContext = createContext<VerifyGate | null>(null);
export const VerifyGateProvider = VerifyGateContext.Provider;
// Null outside a NoteDocument.
export const useVerifyGate = (): VerifyGate | null =>
  useContext(VerifyGateContext);

const inScope = (
  contexts: VerifyMarkerContext[],
  text: string,
  action: VerifyGatedAction,
): VerifyMarkerContext[] => {
  if (!action.scope) return contexts;
  const { first, count } = action.scope(text);
  return contexts.filter(
    (marker) => marker.index >= first && marker.index < first + count,
  );
};

const markersIn = (text: string, action: VerifyGatedAction) =>
  inScope(verifyMarkerContexts(text, stripMarkdown), text, action);

interface Options {
  result: string | null | undefined;
  // Saves the note; resolves true when saved. Without it (an earlier
  // version, or busy), confirming applies to this copy or export only.
  onSave?: (value: string) => Promise<boolean>;
  // Opens the editor at the n-th marker. Without it there is no Edit.
  onEdit?: (index: number) => void;
  // A red allergy flag on the visit: Copy and Export say the plan may
  // include a drug the patient is allergic to, not "unclear values".
  allergyFlagged?: boolean;
}

interface Confirmed {
  key: number;
  before: string;
  after: string;
}

// Copy and Export can skip the review (VITE_REVIEW_COPY_ANYWAY_ENABLED).
export const canGoAnyway = (action: VerifyGatedAction): boolean =>
  AppConfig.enableReviewCopyAnyway &&
  (action.reason === "copy" || action.reason === "export");

export const useVerifyReview = ({
  result,
  onSave,
  onEdit,
  allergyFlagged = false,
}: Options): { gate: VerifyGate; dialog: JSX.Element } => {
  const { t } = useTranslation();
  const [pending, setPending] = useState<VerifyGatedAction | null>(null);
  const [text, setText] = useState("");
  const [total, setTotal] = useState(0);
  const [confirmed, setConfirmed] = useState<Confirmed[]>([]);
  const [saving, setSaving] = useState<number | null>(null);
  // Copy anyway: the action and note waiting on the "not reviewed" sheet.
  const [unreviewed, setUnreviewed] = useState<{
    action: VerifyGatedAction;
    text: string;
    count: number;
  } | null>(null);

  const openReview = (action: VerifyGatedAction, current: string): void => {
    setText(current);
    setTotal(markersIn(current, action).length);
    setConfirmed([]);
    setSaving(null);
    setPending(action);
  };

  const gate: VerifyGate = {
    request: (action) => {
      const current = result ?? "";
      const markers = markersIn(current, action);
      if (!markers.length) {
        action.proceed(current);
        return;
      }
      if (canGoAnyway(action)) {
        setUnreviewed({ action, text: current, count: markers.length });
        return;
      }
      openReview(action, current);
    },
  };

  const remaining = pending ? markersIn(text, pending) : [];
  const resolved = !!pending && remaining.length === 0;

  const close = (): void => {
    if (saving === null) setPending(null);
  };

  const confirm = async (marker: VerifyMarkerContext): Promise<void> => {
    const next = removeVerifyMarker(text, marker.index);
    if (onSave) {
      setSaving(marker.index);
      let saved = false;
      try {
        saved = await onSave(next);
      } catch {
        saved = false;
      }
      setSaving(null);
      // A failed save keeps the marker; the save shows its own error.
      if (!saved) return;
    }
    setText(next);
    setConfirmed((list) => [
      ...list,
      {
        key: Date.now() + list.length,
        before: marker.before,
        after: marker.after,
      },
    ]);
  };

  const edit = (index: number): void => {
    setPending(null);
    onEdit?.(index);
  };

  const primary = (): void => {
    if (!pending) return;
    if (resolved) {
      const action = pending;
      setPending(null);
      action.proceed(text);
      return;
    }
    const first = remaining[0];
    if (first) edit(first.index);
  };

  // Creating a summary or letter offers "Resolve now" (the editor at the
  // first value) until everything is resolved; Copy and Export wait.
  const generating =
    pending?.reason === "summary" || pending?.reason === "letter";
  const primaryLabel = resolved
    ? pending?.label
    : generating && onEdit
      ? t("visit.workspace.verify.review.resolveNow")
      : pending?.label;
  const primaryDisabled =
    saving !== null || (!resolved && !(generating && onEdit));

  const valueLabel = (marker: { before: string }) =>
    marker.before.replace(/^…/, "").split(" ").slice(-4).join(" ");

  const v3 = AppConfig.enableAppRedesign === true;
  const quote = (marker: { before: string; after: string }, done = false) => (
    <p
      className={cn(
        "min-w-0 wrap-break-word text-sm leading-relaxed",
        v3 && "font-v3 text-[14.5px] text-v3-text",
        v3 && done && "text-v3-text-3",
      )}
    >
      {marker.before}{" "}
      {done ? (
        <span
          className={
            v3
              ? V3_DONE_CHIP
              : "inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 px-1.5 align-baseline text-[0.8em] font-medium text-emerald-900 dark:border-emerald-400/40 dark:bg-emerald-400/15 dark:text-emerald-100"
          }
        >
          <Check aria-hidden className="size-3" />
          {t("visit.workspace.verify.review.confirmed")}
        </span>
      ) : (
        <span className={v3 ? V3_CHIP : VERIFY_CHIP_CLASS}>
          {t("visit.workspace.verify.chip")}
        </span>
      )}
      {marker.after &&
        (/^[,.;:)]/.test(marker.after) ? marker.after : ` ${marker.after}`)}
    </p>
  );

  const dialog = (
    <Dialog
      open={!!pending}
      onOpenChange={(open) => {
        if (!open) close();
      }}
    >
      <DialogContent
        className="flex max-h-[min(90dvh,44rem)] w-[calc(100%-2rem)] max-w-lg flex-col gap-4 rounded-2xl p-5 sm:p-6 [&>button.absolute]:right-2 [&>button.absolute]:top-2 [&>button.absolute]:flex [&>button.absolute]:size-11 [&>button.absolute]:items-center [&>button.absolute]:justify-center"
        data-testid="verify-review"
      >
        <DialogHeader className="space-y-2 pr-8 text-left">
          <DialogTitle>
            {t("visit.workspace.verify.review.title", { count: total })}
          </DialogTitle>
          <DialogDescription className="text-sm">
            {pending &&
              t(
                allergyFlagged &&
                  (pending.reason === "copy" || pending.reason === "export")
                  ? `visit.workspace.verify.review.${pending.reason}Allergy`
                  : `visit.workspace.verify.review.${pending.reason}`,
              )}
          </DialogDescription>
          {!onSave && (
            <p className="text-sm text-muted-foreground">
              {t("visit.workspace.verify.review.readOnly")}
            </p>
          )}
        </DialogHeader>
        <div className="-mx-1 min-h-0 flex-1 overflow-y-auto px-1">
          <p
            aria-live="polite"
            className="mb-2 text-xs font-medium text-muted-foreground"
            data-testid="verify-review-progress"
          >
            {t("visit.workspace.verify.review.progress", {
              done: total - remaining.length,
              total,
              count: total,
            })}
          </p>
          <ul
            aria-label={t("visit.workspace.verify.review.list")}
            className="space-y-2"
          >
            {remaining.map((marker) => (
              <li
                key={`open-${marker.index}`}
                className={
                  v3
                    ? "rounded-xl bg-v3-panel p-3 shadow-v3-hairline-strong"
                    : "rounded-xl border border-amber-200 bg-amber-50/60 p-3 dark:border-amber-400/30 dark:bg-amber-400/5"
                }
                data-testid="verify-review-item"
              >
                {quote(marker)}
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button
                    aria-label={t("visit.workspace.verify.review.confirmAria", {
                      value: valueLabel(marker),
                    })}
                    className="h-11 flex-1 sm:flex-none lg:h-9"
                    disabled={saving !== null}
                    size="sm"
                    type="button"
                    variant="outline"
                    onClick={() => void confirm(marker)}
                  >
                    {saving === marker.index ? (
                      <LoadingSpinner />
                    ) : (
                      <Check aria-hidden />
                    )}
                    {t("visit.workspace.verify.review.confirm")}
                  </Button>
                  {onEdit && (
                    <Button
                      aria-label={t("visit.workspace.verify.review.editAria", {
                        value: valueLabel(marker),
                      })}
                      className="h-11 flex-1 sm:flex-none lg:h-9"
                      disabled={saving !== null}
                      size="sm"
                      type="button"
                      variant="outline"
                      onClick={() => edit(marker.index)}
                    >
                      <Pencil aria-hidden />
                      {t("visit.workspace.verify.review.edit")}
                    </Button>
                  )}
                </div>
              </li>
            ))}
            {confirmed.map((marker) => (
              <li
                key={`done-${marker.key}`}
                className={
                  v3
                    ? "rounded-xl bg-v3-surface-2 p-3"
                    : "rounded-xl border p-3 text-muted-foreground"
                }
                data-testid="verify-review-confirmed"
              >
                {quote(marker, true)}
              </li>
            ))}
          </ul>
        </div>
        <DialogFooter className="gap-2 sm:items-center sm:gap-0">
          <Button
            className="h-11 lg:h-10"
            disabled={saving !== null}
            type="button"
            variant="ghost"
            onClick={close}
          >
            {t("common.cancel")}
          </Button>
          {pending && !resolved && canGoAnyway(pending) && (
            <Button
              className="h-11 lg:h-10"
              data-testid="verify-review-anyway"
              disabled={saving !== null}
              type="button"
              variant="outline"
              onClick={() => {
                const action = pending;
                setPending(null);
                action.proceed(text);
              }}
            >
              {pending.reason === "copy"
                ? t("visit.workspace.verify.bulk.copyAnyway")
                : t("visit.workspace.verify.bulk.exportAnyway")}
            </Button>
          )}
          <Button
            className={cn("h-11 lg:h-10")}
            data-testid="verify-review-primary"
            disabled={primaryDisabled}
            type="button"
            onClick={primary}
          >
            {primaryLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  const sheet = (
    <UnreviewedSheet
      allergy={allergyFlagged}
      count={unreviewed?.count ?? 0}
      kind={unreviewed?.action.reason === "export" ? "export" : "copy"}
      open={!!unreviewed}
      onCancel={() => setUnreviewed(null)}
      onProceed={() => {
        if (!unreviewed) return;
        setUnreviewed(null);
        unreviewed.action.proceed(unreviewed.text);
      }}
      onReview={() => {
        if (!unreviewed) return;
        setUnreviewed(null);
        openReview(unreviewed.action, unreviewed.text);
      }}
    />
  );

  return {
    gate,
    dialog: (
      <>
        {dialog}
        {sheet}
      </>
    ),
  };
};
